import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { onAuthStateChanged } from 'firebase/auth';
import 'firebase/auth';
import { logout } from 'src/app/actions';
import { RootState } from 'src/app/store';
import {
  addNewBrandRole,
  addNewLocationRole,
  addNewRole,
  authentication,
  brandsDb,
  checkActiveUser,
  locationsDb,
  servicerAppDb,
  updateBrandRole,
  updateLocationRole,
  updateRole,
} from 'src/config/firebaseConfig';
import { UserProfile } from 'src/interfaces/user';
import { setClientType, updateSelectedFilter } from './filterSlice';

interface AuthState {
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
  status: 'idle' | 'loading' | 'loggedIn' | 'loggedOut';
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  status: 'idle',
};

export const updateRoleToFirebase = createAsyncThunk(
  'auth/updateRole',
  async (
    { tab, data, id }: { tab: number; data: any; id: string },
    { getState, rejectWithValue },
  ) => {
    const { selectedFilter, interactions } = getState().filters;
    try {
      let response = null;
      if (tab == 1) {
        response = id
          ? await updateRole(brandsDb, id, data)
          : addNewRole(brandsDb, data);
      } else if (tab == 3) {
        response = id
          ? await updateRole(locationsDb, id, data)
          : addNewRole(locationsDb, data);
      } else if (tab == 5) {
        response = id
          ? await updateRole(servicerAppDb, id, data)
          : addNewRole(servicerAppDb, data);
      }

      console.log('role response--', response);
      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue('Error updating Role');
    }
  },
);
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signIn: (state, action: PayloadAction<UserProfile>) => {
      state.user = action.payload;
      localStorage.setItem('authUser', JSON.stringify(action.payload)); // Save session
    },
    signOut: (state) => {
      state.user = null;
      localStorage.removeItem('authUser'); // Clear session on logout
    },
    updateActiveBrand: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.dash.activeBrand = action.payload;
        localStorage.setItem('authUser', JSON.stringify(state.user)); // Save session
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialState) // Reset state on logout
      .addCase(updateRoleToFirebase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRoleToFirebase.fulfilled, (state, action) => {
        const data = action.payload;

        console.log('role state', data);
        state.loading = false;
      })
      .addCase(updateRoleToFirebase.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message ||
          'Failed to fetch Interactions ' + action.meta.arg.url;
      });
  },
});
// Restore session from localStorage before Firebase loads
export const restoreSession = () => (dispatch: any) => {
  const storedUser = localStorage.getItem('authUser');
  if (storedUser) {
    dispatch(signIn(JSON.parse(storedUser))); // Restore user session instantly
  }
};
export const logoutUser = () => async (dispatch: any) => {
  dispatch(signOut());
};

// Hook to monitor authentication state
// export const monitorAuthState =
//   () => (dispatch: any, getState: () => RootState) => {
//     const currentState = getState().auth.user;
//     onAuthStateChanged(authentication, async (user) => {
//       if (!user || !currentState || currentState.uid !== user.uid) {
//         if (user) {
//           const snapshot = await checkActiveUser(user.uid);
//           dispatch(setClientType(snapshot.dash.activeType));
//           dispatch(signIn({ ...snapshot, uid: user.uid }));
//         }
//       } else {
//         dispatch(signOut());
//       }
//     });
//   };
export const monitorAuthState = () => (dispatch: any, getState: any) => {
  onAuthStateChanged(authentication, async (user) => {
    if (user) {
      const storedUser = localStorage.getItem('authUser');
      // Prevent unnecessary re-fetch if session is the same
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.uid === user.uid) {
          dispatch(signIn(parsedUser));
          return;
        }
      }
      // Fetch fresh data only if necessary
      const snapshot = await checkActiveUser(user.uid);
      const userData = { ...snapshot, uid: user.uid };
      localStorage.setItem('authUser', JSON.stringify(userData)); // Update stored session
      if (snapshot.dash) {
        dispatch(setClientType(snapshot.dash.activeType));
        dispatch(signIn(userData));
      }
    } else {
      localStorage.removeItem('authUser'); // Clear session on sign-out
      dispatch(signOut());
    }
  });
};

export const { signIn, signOut, updateActiveBrand } = authSlice.actions;
export default authSlice.reducer;
